// Transport-independent contract for the installed Vendor lifecycle V2 API.
// No retries: mutations always retain the revision the user actually reviewed.
export const LIFECYCLE_FIELDS = Object.freeze([
  'title', 'description', 'document_type', 'version_label',
  'effective_date', 'expiry_date', 'review_date',
]);
export function lifecycleId(value, zero = false) {
  const text = String(value ?? '');
  if ((typeof value === 'number' && !Number.isSafeInteger(value)) ||
      !(zero ? /^(0|[1-9]\d*)$/ : /^[1-9]\d*$/).test(text) ||
      BigInt(text) > 9223372036854775807n) throw new Error('Invalid document reference or revision. Refresh the list.');
  return text;
}
export function lifecycleDraft(document = {}) {
  return Object.fromEntries(LIFECYCLE_FIELDS.map(key => [key,
    key.endsWith('_date') ? String(document[key] || '').slice(0, 10) : String(document[key] ?? '')]));
}
export function lifecyclePayload(document, values, reason, metadata = true) {
  if (typeof reason !== 'string' || reason.trim().length < 3 || reason.trim().length > 500) {
    throw new Error('Enter a reason between 3 and 500 characters.');
  }
  const body = { expected_revision: lifecycleId(document.lifecycle_revision, true), reason: reason.trim() };
  if (!metadata) return body;
  for (const key of LIFECYCLE_FIELDS) {
    const raw = values[key];
    if (typeof raw !== 'string') throw new Error('Check the document details.');
    const value = raw.trim();
    if (key.endsWith('_date')) {
      if (value) {
        const date = new Date(`${value}T00:00:00Z`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000') ||
            Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
          throw new Error('Enter valid calendar dates.');
        }
      }
    } else {
      const limit = { title: 200, description: 2000, version_label: 50, document_type: 80 }[key];
      if (value.length > limit || value.includes('\0')) throw new Error(`Check ${key.replaceAll('_', ' ')}.`);
      if (key === 'title' && !value) throw new Error('A document title is required.');
      // Preserve a valid existing category even when it is not in the UI presets.
      if (key === 'document_type' && !/^[a-z][a-z0-9_]*$/.test(value)) throw new Error('Choose a valid category.');
    }
    body[key] = value || null;
  }
  if (body.effective_date && body.expiry_date && body.expiry_date < body.effective_date) {
    throw new Error('Expiry cannot be before the effective date.');
  }
  return body;
}
export function lifecycleDocument(document, companyId, expectedId) {
  lifecycleId(document?.document_id);
  lifecycleId(document?.company_id);
  lifecycleId(document?.lifecycle_revision, true);
  lifecycleId(document?.file_version);
  if (document.unit_id !== null || typeof document.is_active !== 'boolean' ||
      (companyId != null && String(document.company_id) !== String(companyId)) ||
      (expectedId != null && String(document.document_id) !== String(expectedId))) {
    throw new Error('Document context changed. Close this panel and reopen the current vendor.');
  }
  return document;
}
export function mutationNeedsReview(error) {
  const status = error?.response?.status;
  return !status || status === 408 || status >= 500;
}
export function lifecycleError(error) {
  const status = error?.response?.status;
  if (status === 409) return 'This document changed, is shared, or cannot be changed in its current state. Refresh and review its history before reopening the action.';
  if (status === 401) return 'Your session is unavailable. Sign in again.';
  if (status === 403) return 'The server denied this document action. Check your access and the selected company.';
  if (status === 404) return 'This document is not available in the current company.';
  if (status === 413) return 'The file exceeds the server upload limit.';
  if (status === 400 || status === 422) return 'The server rejected the file or details. Check its format, dates and reason.';
  if (status >= 500 || error?.isAxiosError) return 'The request could not be confirmed. Refresh the list and check History before trying again.';
  return error?.message || 'The document action could not be completed.';
}

export function createDocumentLifecycleApi(client) {
  const base = '/documents/lifecycle';
  const headers = { 'X-Requested-With': 'XMLHttpRequest' };
  const read = async (path, signal, params = {}) => (await client.get(`${base}${path}`, {
    signal, timeout: 20000, params: { ...params, _ts: Date.now() },
  })).data;
  async function saved(response, current, companyId) {
    const result = lifecycleDocument(response.data, companyId, current.document_id);
    if (BigInt(result.lifecycle_revision) !== BigInt(current.lifecycle_revision) + 1n) {
      throw new Error('Save result is uncertain. Refresh and inspect History.');
    }
    return result;
  }
  return {
    async capabilities(signal) {
      const data = await read('/capabilities', signal);
      if (data?.version !== 2 || data.ready !== true || !data.entity_types?.includes('vendor') ||
          !['metadata', 'archive', 'restore', 'replacement_history'].every(key => data[key] === true)) {
        throw new Error('Vendor document management V2 is not available on this backend.');
      }
      return data;
    },
    async list(vendorId, { status = 'active', before = null, signal, companyId } = {}) {
      if (!['active', 'archived', 'all'].includes(status)) throw new Error('Invalid status filter.');
      const data = await read(`/vendor/${lifecycleId(vendorId)}`, signal, {
        status, ...(before ? { before: lifecycleId(before) } : {}),
      });
      if (!Array.isArray(data?.documents) || data.count !== data.documents.length) throw new Error('Unexpected document list.');
      data.documents.forEach(doc => {
        lifecycleDocument(doc, companyId);
        if ((status === 'active' && !doc.is_active) || (status === 'archived' && doc.is_active)) throw new Error('Unexpected document status.');
      });
      const cursor = data.next_cursor == null ? null : lifecycleId(data.next_cursor);
      if (cursor && (!data.documents.length || (before && BigInt(cursor) >= BigInt(before)))) throw new Error('Invalid document page cursor.');
      return { documents: data.documents, next_cursor: cursor };
    },
    async history(id, { signal, companyId } = {}) {
      const data = await read(`/${lifecycleId(id)}/history`, signal);
      lifecycleDocument(data?.document, companyId, id);
      if (!Array.isArray(data?.versions) || !Array.isArray(data?.events)) throw new Error('Unexpected document history.');
      return data;
    },
    async edit(current, values, reason, companyId) {
      const body = lifecyclePayload(current, values, reason);
      return saved(await client.patch(`${base}/${lifecycleId(current.document_id)}`, body, { headers }), current, companyId);
    },
    async setArchived(current, archive, reason, companyId) {
      const body = lifecyclePayload(current, {}, reason, false);
      const result = await saved(await client.patch(`${base}/${lifecycleId(current.document_id)}/${archive ? 'archive' : 'restore'}`, body, { headers }), current, companyId);
      if (result.is_active !== !archive) throw new Error('Document status could not be confirmed. Refresh and check History.');
      return result;
    },
    async replace(current, file, mime, values, reason, companyId, onUploadProgress) {
      const fields = lifecyclePayload(current, values, reason);
      const body = new FormData();
      for (const [key, value] of Object.entries(fields)) body.append(key, value ?? '');
      body.append('file', file.slice(0, file.size, mime), file.name);
      const response = await client.post(`${base}/${lifecycleId(current.document_id)}/replace`, body, { headers, onUploadProgress });
      const result = await saved(response, current, companyId);
      if (response.status !== 201 || BigInt(result.file_version) !== BigInt(current.file_version) + 1n) throw new Error('Replacement result is uncertain. Check History before retrying.');
      return result;
    },
    async file(documentId, version, signal) {
      const { data } = await client.get(`${base}/${lifecycleId(documentId)}/versions/${lifecycleId(version)}/file`, {
        responseType: 'blob', signal, params: { _ts: Date.now() },
      });
      if (!(data instanceof Blob)) throw new Error('The file response was not valid.');
      return data;
    },
  };
}
